import React from "react";
import { Area, Line } from "recharts";
import { Info } from "lucide-react";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtNum, fmtPeriod, applyMult } from "./theme";
import { DeltaBadge } from "./components";

/** Pulse Forecast (M4) — shared helpers. Forecasts are computed by Mission Control
 *  (Timestral) and stored in TI with provenance; the UI only renders them, always
 *  with their date, and applies the confidence rule: alta = value + band,
 *  media = band only, bassa = nothing but the volatility note. */

export const showsBand = (fc) => !!fc && fc.display_rule !== "hide";
export const showsMedian = (fc) => !!fc && fc.display_rule === "value_and_band";

export const FC_STROKE = "#C2410C";
export const FC_BAND = "rgba(194,65,12,0.18)";

/** Rows for a ComposedChart: observed rows first, then one row per forecast period
 *  with `fq50` (median) and `band` = [q10, q90]. The last observed point also carries
 *  fq50 so the dashed line starts from the real series. */
export const withForecastRows = (observedRows, fc, lang, valueKey = "value", mult = 1) => {
  if (!showsBand(fc) || !fc.points?.length) return observedRows;
  const rows = observedRows.map((r) => ({ ...r }));
  const last = rows[rows.length - 1];
  if (last && showsMedian(fc) && last[valueKey] !== null && last[valueKey] !== undefined) last.fq50 = last[valueKey];
  fc.points.forEach((p) => rows.push({
    period: fmtPeriod(p.period, lang), raw: p.period, forecast: true,
    fq50: showsMedian(fc) ? p.q50 * mult : undefined,
    band: [p.q10 * mult, p.q90 * mult],
  }));
  return rows;
};

/** Recharts elements for the forecast overlay (spread inside a ComposedChart). */
export const forecastLayers = (fc, t) => {
  if (!showsBand(fc)) return null;
  return [
    <Area key="band" type="monotone" dataKey="band" name={t("fc_band")} stroke="none"
      fill={FC_BAND} fillOpacity={1} connectNulls={false} isAnimationActive={false} legendType="none" />,
    showsMedian(fc) && (
      <Line key="fq50" type="monotone" dataKey="fq50" name={t("fc_median")} stroke={FC_STROKE}
        strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls={false} isAnimationActive={false} />
    ),
  ].filter(Boolean);
};

/** Tooltip that understands the band array. */
export const ForecastTooltip = ({ active, payload, label, dm, formatter }) => {
  const { t } = usePulseLocale();
  const T = tokens(dm);
  if (!active || !payload?.length) return null;
  const isFc = payload.some((p) => p.payload?.forecast);
  return (
    <div className="px-3 py-2 text-xs" style={{ background: dm ? "#0F1115" : "#111827", color: "#F3F4F6",
      border: `1px solid ${T.border}`, borderRadius: 2 }}>
      <div className="font-semibold mb-1">{label}{isFc ? ` · ${t("fc_tag")}` : ""}</div>
      {payload.filter((p) => p.value !== undefined && p.value !== null).map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span style={{ width: 8, height: 8, background: p.color || FC_STROKE, display: "inline-block" }} />
          <span>{p.name}:</span>
          <span className="pulse-num font-semibold">
            {Array.isArray(p.value) ? `${formatter(p.value[0])} – ${formatter(p.value[1])}` : formatter(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

/** Provenance line under a chart: «Previsione · Timestral · calcolata il gg/mm · base dati vN»
 *  + confidence badge; `bassa` → only the volatility sentence. */
export const ForecastNote = ({ fc, dm, testid }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  if (!fc) return null;
  const conf = fc.confidence;
  const badgeBg = conf === "alta" ? T.brandSoft : (conf === "media" ? T.expBg : T.surface2);
  const badgeFg = conf === "alta" ? T.accent : (conf === "media" ? T.expText : T.muted);
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid={testid || "forecast-note"}>
      <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5"
        style={{ background: "rgba(194,65,12,0.10)", color: FC_STROKE, borderRadius: 2 }}>
        {fc.label}
      </span>
      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 cursor-help"
        title={t(`fc_conf_${conf}_tip`)} style={{ background: badgeBg, color: badgeFg, borderRadius: 2 }}
        data-testid="forecast-confidence">
        {t("fc_confidence")}: {t(`fc_conf_${conf}`)}
      </span>
      {conf === "bassa" && (
        <span className="text-[11px]" style={{ color: T.muted }} data-testid="forecast-volatile">{t("fc_too_volatile")}</span>
      )}
      {conf !== "bassa" && (
        <span className="text-[10px] inline-flex items-center gap-1" style={{ color: T.muted }}>
          <Info className="w-3 h-3" aria-hidden="true" />
          {conf === "media" ? t("fc_band_only_note") : t("fc_band_note")}
          {fc.backtest?.mape !== null && fc.backtest?.mape !== undefined ? ` · MAPE ${fmtNum(fc.backtest.mape, lang, 1)}%` : ""}
        </span>
      )}
    </div>
  );
};


/** Teaser / landing KPI card for a forecast_kpis entry (confidence=alta only — the caller
 *  filters). Shows the 6-month median, YoY vs observed, the 10–90 band and the provenance label. */
export const ForecastKpiCard = ({ fk, dm, testidPrefix = "teaser-kpi-forecast" }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const eur = fk.unit === "EUR" ? " €" : "";
  const val = `${fmtCompact(applyMult(fk.value, fk.unit_mult), lang)}${eur}`;
  return (
    <div className="p-5 flex flex-col gap-2" style={{ background: T.surface }} data-testid={`${testidPrefix}-${fk.id}`}>
      <span className="text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1" style={{ color: T.muted }}>
        {lang === "it" ? fk.label_it : fk.label_en}
        <Info className="w-3.5 h-3.5 cursor-help" style={{ color: T.accent }} aria-label={lang === "it" ? fk.tooltip_it : fk.tooltip_en}
          title={lang === "it" ? fk.tooltip_it : fk.tooltip_en} />
      </span>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="pulse-num text-3xl md:text-4xl font-semibold" data-testid={`${testidPrefix}-value-${fk.id}`}>{val}</span>
        <DeltaBadge pct={fk.yoy_pct} dm={dm} suffix={lang === "it" ? "vs stessi mesi anno prima" : "vs same months last year"} />
      </div>
      <span className="text-xs pulse-num" style={{ color: T.muted }} data-testid={`${testidPrefix}-band-${fk.id}`}>
        {t("fc_band")}: {fmtCompact(applyMult(fk.band[0], fk.unit_mult), lang)} – {fmtCompact(applyMult(fk.band[1], fk.unit_mult), lang)}{eur} · {lang === "it" ? fk.period_it : fk.period_en}
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 self-start"
        style={{ background: "rgba(194,65,12,0.10)", color: FC_STROKE, borderRadius: 2 }} data-testid={`${testidPrefix}-label-${fk.id}`}>
        {lang === "it" ? fk.forecast_label_it : fk.forecast_label_en}
      </span>
    </div>
  );
};
